import type {MatchParams} from '@wandb/weave/common/types/base';
import {useCallback, useMemo} from 'react';

import {
  getTrackContext,
  trackAgentChatInteraction,
} from '../../services/analytics/agentChatEvents';

/**
 * useTrackWBAgentQueue binds the queued-message trackers to the current route
 * and thread. Depths and positions describe the queue as the user saw it, so
 * callers read them before the action mutates the queue.
 */
export function useTrackWBAgentQueue(
  params: MatchParams,
  threadId: string | undefined
) {
  return useMemo(() => {
    const base = getTrackContext(params, threadId);

    const trackRow =
      (action: QueueRowAction) =>
      (queueDepth: number, queuePosition?: number) => {
        trackAgentChatInteraction({
          ...base,
          action,
          queue_depth: queueDepth,
          queue_position: queuePosition,
        });
      };

    return {
      // NOTE: a queued submission fires both this and `message_sent`, so a
      // queued send counts once as a send and once as queued.
      trackQueued: (queueDepth: number) => {
        trackAgentChatInteraction({
          ...base,
          action: 'message_queued',
          queue_depth: queueDepth,
        });
      },
      trackRecalled: trackRow('queued_message_recalled'),
      trackRemoved: trackRow('queued_message_removed'),
      trackSteered: trackRow('queued_message_steered'),
    };
  }, [params, threadId]);
}

/**
 * useTrackWBAgentQueueDrain records the drainer dispatching a queued message.
 * A send the server never confirms leaves the item queued, and a later retry
 * records again. The drainer spans every thread at once, so the tracker takes
 * its thread per call instead of binding one.
 */
export function useTrackWBAgentQueueDrain(
  params: MatchParams
): (threadId: string, queueDepth: number) => void {
  return useCallback(
    (threadId, queueDepth) => {
      trackAgentChatInteraction({
        ...getTrackContext(params, threadId),
        action: 'queued_message_sent',
        queue_depth: queueDepth,
      });
    },
    [params]
  );
}

/** QueueRowAction is an action a user takes on one queued row's controls. */
type QueueRowAction =
  | 'queued_message_recalled'
  | 'queued_message_removed'
  | 'queued_message_steered';
